import { apiFetch, ApiError } from "@pontimed/api-client";

import {
  adoptNativeSession,
  clearNativeSession,
  nativeRefreshToken,
  usesNativeSession,
} from "./native-session";

// Singleton in-flight promise — coalesces concurrent callers into a single
// POST /api/auth/refresh. Without this, the 14-min background timer + the
// useSession 401-retry path can race: both consume the same pontimed_rt,
// the second hits the tombstone, the /refresh handler clears both cookies,
// and whichever response lands last wins → ~50% logout on rotation tick.
let inFlight: Promise<boolean> | null = null;

/**
 * Calls apps/api POST /api/auth/refresh. The server rotates the refresh
 * cookie + re-mints the access cookie.
 *
 * Returns:
 *  - `true` on success.
 *  - `false` ONLY on real 401 (refresh token missing/invalid/replayed) — in
 *    that case the server has cleared cookies and the user is genuinely
 *    logged out.
 *
 * Throws on every other failure (network blip, timeout, 5xx, CORS) so
 * callers don't mistake a transient hiccup for a logout. That includes the
 * 503 apps/api answers while another request (a second tab) is rotating the
 * same refresh token — apiFetch retries it, and the retry gets that rotation's
 * answer; the cookies are never cleared for it — and the 503
 * `REFRESH_UNAVAILABLE` it answers when a rotation failed on its database or
 * Redis without spending the token (the retry rotates normally). Without this,
 * a single flaky refresh tick was flipping `useSession` to
 * `unauthenticated` and popping the auth modal even though the 30-day
 * refresh cookie was perfectly valid.
 *
 * Web: refresh token is in `pontimed_rt` HttpOnly cookie scoped to
 * /api/auth/refresh — browser sends it automatically.
 * Native app (./native-session.ts): the refresh token goes in the JSON body
 * (`refreshToken`) and the rotated pair comes back in the body (`session`),
 * kept in the app's store; a 401 forgets it.
 */
/**
 * Why the most recent refresh was refused, when the server said. apps/api
 * answers a session that hygiene ended (90-day cap, professional idle
 * timeout) with 401 + `{ code: "SESSION_EXPIRED", reason }` instead of the
 * generic 401 for a bad token. `refreshSession` still returns a plain boolean
 * — every caller only needs that — but the reason is parked here so the auth
 * modal can tell the user "your session expired, sign in again" rather than
 * appearing out of nowhere. Read-once: `takeLastRefreshFailure` clears it.
 */
export interface RefreshFailure {
  code?: string;
  reason?: string;
}

let lastRefreshFailure: RefreshFailure | null = null;

export function takeLastRefreshFailure(): RefreshFailure | null {
  const f = lastRefreshFailure;
  lastRefreshFailure = null;
  return f;
}

function rememberRefreshFailure(body: unknown): void {
  if (body && typeof body === "object") {
    const { code, reason } = body as { code?: unknown; reason?: unknown };
    lastRefreshFailure = {
      code: typeof code === "string" ? code : undefined,
      reason: typeof reason === "string" ? reason : undefined,
    };
    return;
  }
  lastRefreshFailure = null;
}

export async function refreshSession(): Promise<boolean> {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    try {
      // Server returns:
      //   - 200 with the new session payload → refreshed OK
      //   - 200 with JSON `null`            → anonymous probe (no refresh
      //                                       cookie present); treat as "not
      //                                       refreshed", identical to the
      //                                       legacy 401.
      //   - 401                              → real refresh failure (invalid
      //                                       / replayed / expired)
      if (usesNativeSession()) {
        const refreshToken = await nativeRefreshToken();
        if (!refreshToken) return false; // signed out on this device
        const body = await apiFetch.post<unknown>(
          "/api/auth/refresh",
          { refreshToken },
          { skipAuthRefresh: true },
        );
        lastRefreshFailure = null;
        return adoptNativeSession(body, { keepReceipts: true });
      }
      const body = await apiFetch.post<unknown>("/api/auth/refresh", undefined, {
        skipAuthRefresh: true,
      });
      lastRefreshFailure = null;
      return body !== null;
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        rememberRefreshFailure(error.body);
        // The server refused the pair for good (revoked, reused, expired):
        // the app must not present it again.
        await clearNativeSession();
        return false;
      }
      throw error;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}
